import { lazy, StrictMode, Suspense, type ComponentType } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter, Route, Routes } from 'react-router-dom'

import './index.css'
import { AppShell } from './components/AppShell'
import { HomeRedirect } from './components/auth/HomeRedirect'
import { PageTitleProvider } from './components/PageTitleProvider'
import { MatchList } from './pages/MatchList'
import { SubTabLayout, type SubTab } from './components/SubTabLayout'

// Code-splitting per trasa. Eager zostaje tylko to, co widać od razu: powłoka
// + lista meczów (trasa startowa). Reszta doczytuje się własnym chunkiem.
//
// Strony eksportują nazwane symbole, a React.lazy wymaga `default` — stąd `lazyPage`.
// Trasy /print są bezpieczne: Playwright czeka na `networkidle` + [data-ready="true"],
// więc doczytanie chunku mieści się w oknie oczekiwania (patrz backend/app/pdf.py).
function lazyPage<M, K extends keyof M>(load: () => Promise<M>, name: K) {
  return lazy(() => load().then((m) => ({ default: m[name] as ComponentType })))
}

const Report = lazyPage(() => import('./pages/Report'), 'Report')
const Print = lazyPage(() => import('./pages/Print'), 'Print')
const TeamList = lazyPage(() => import('./pages/TeamList'), 'TeamList')
const TeamDossier = lazyPage(() => import('./pages/TeamDossier'), 'TeamDossier')
const DossierPrint = lazyPage(() => import('./pages/DossierPrint'), 'DossierPrint')
const PlayerList = lazyPage(() => import('./pages/PlayerList'), 'PlayerList')
const PlayerProfile = lazyPage(() => import('./pages/PlayerProfile'), 'PlayerProfile')
const Motoryka = lazyPage(() => import('./pages/Motoryka'), 'Motoryka')
const Kadra = lazyPage(() => import('./pages/Kadra'), 'Kadra')
const Account = lazyPage(() => import('./pages/Account'), 'Account')
const SetPassword = lazyPage(() => import('./pages/SetPassword'), 'SetPassword')
const AdminUsers = lazyPage(() => import('./pages/AdminUsers'), 'AdminUsers')

// Analiza przeciwnika — dossier drużyn I ligi + profile zawodników ligi.
const ANALIZA_SUBTABS: SubTab[] = [
  { to: '/dossier', label: 'Drużyny' },
  { to: '/gracze', label: 'Zawodnicy' },
]

// Widoczne tylko w oknie doczytywania chunku trasy — celowo bez spinnera i bez
// `data-ready`, żeby nie mignąć w PDF-ie ani nie oszukać czekania Playwrighta.
// Inline (nie osobny komponent), bo main.tsx nic nie eksportuje — react-refresh
// wymaga wtedy, by w pliku nie było definicji komponentów.
const routeFallback = <div className="p-6 text-sm text-ink-400">Wczytywanie…</div>

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <BrowserRouter>
      {/* Tytuł karty per trasa (lib/pageTitle.ts) — także /print, bo z niego tytuł PDF-u. */}
      <PageTitleProvider>
      <Suspense fallback={routeFallback}>
        <Routes>
          {/* Print routes — no shell, no chrome, just the renderable surface */}
          <Route path="/raporty/:matchId/print" element={<Print />} />
          <Route path="/dossier/:teamId/print" element={<DossierPrint />} />

          {/* All other surfaces sit inside the shell */}
          <Route element={<AppShell />}>
            <Route path="/" element={<HomeRedirect />} />
            <Route path="/raporty" element={<MatchList />} />
            <Route path="/raporty/:matchId" element={<Report />} />

            {/* Analiza przeciwnika — listy dzielą pasek; szczegóły (dossier/:id,
                gracze/:id) są poza paskiem — mają własne linki „← wróć". */}
            <Route element={<SubTabLayout tabs={ANALIZA_SUBTABS} />}>
              <Route path="/dossier" element={<TeamList />} />
              <Route path="/gracze" element={<PlayerList />} />
            </Route>
            <Route path="/dossier/:teamId" element={<TeamDossier />} />
            <Route path="/gracze/:playerId" element={<PlayerProfile />} />

            <Route path="/motoryka" element={<Motoryka />} />
            <Route path="/kadra" element={<Kadra />} />

            {/* Konta i dostęp — bez zakładki w belce (menu konta): /konto (własne
                konto, zmiana hasła) i /admin (konta, role, macierz; tylko admin). */}
            <Route path="/konto" element={<Account />} />
            {/* Hasło z linku z maila (zaproszenie / reset): token po „#" (#t=…). */}
            <Route path="/konto/ustaw-haslo" element={<SetPassword />} />
            <Route path="/admin" element={<AdminUsers />} />
            <Route path="*" element={<HomeRedirect />} />
          </Route>
        </Routes>
      </Suspense>
      </PageTitleProvider>
    </BrowserRouter>
  </StrictMode>,
)
